import { getFindexSessionId } from "@/modules/auth/hooks/useSession";
import { getErrorMessage } from "@/utils/error";
import { pushToast } from "components/toast/hooks";

export type APIClientRequestContext = { response?: Response; error?: Error | unknown };
export type APIClientReturn<T = any> = [T | undefined, APIClientRequestContext, errorMessage: string | undefined];
export type APIClientRequestArgs = { request?: RequestInit; payload?: any; };

const RATE_LIMIT_DEFAULT_MESSAGE = 'Too many requests, please try again in a few minutes.';
const RATE_LIMIT_TOAST_DEDUPE_MS = 5_000;

// Module-level so concurrent 429s across all client instances collapse into a
// single toast. performance.now() is monotonic — Date.now() can jump backward
// on NTP/DST adjustments and permanently freeze the dedupe window.
let lastRateLimitToastAt = -Infinity;

// Shared across all BaseAPIClientV2 instances so concurrent identical GETs
// from FindexClient and IRClient (different instances, same backend URL)
// collapse to one network request. Entries live only for the duration of the
// in-flight fetch; results are not cached across ticks.
const inFlightGets = new Map<string, Promise<APIClientReturn<unknown>>>();

const formatRetryAfter = (header: string | null): string | null => {
  if (!header) return null;

  let seconds = Number(header);
  if (!Number.isFinite(seconds)) {
    const epoch = Date.parse(header);
    if (Number.isNaN(epoch)) return null;
    seconds = Math.max(0, (epoch - Date.now()) / 1000);
  }
  if (seconds <= 0) return null;

  if (seconds < 60) {
    const whole = Math.ceil(seconds);
    return `Try again in ${whole} second${whole === 1 ? '' : 's'}.`;
  }
  const minutes = Math.ceil(seconds / 60);
  return `Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
};

const extractServerMessage = (data: unknown): string | undefined => {
  if (typeof data === 'string') return data || undefined;
  if (data && typeof data === 'object' && 'message' in data) {
    const message = (data as { message?: unknown }).message;
    if (typeof message === 'string' && message) return message;
  }
  return undefined;
};

const handleRateLimit = (resp: Response, serverMessage?: string): string => {
  const message = serverMessage || RATE_LIMIT_DEFAULT_MESSAGE;
  const retryHint = formatRetryAfter(resp.headers.get('Retry-After'));
  const subtitle = retryHint ? `${message} ${retryHint}` : message;

  const now = performance.now();
  if (now - lastRateLimitToastAt > RATE_LIMIT_TOAST_DEDUPE_MS) {
    lastRateLimitToastAt = now;
    pushToast({
      title: 'Slow down',
      subtitle,
      variant: 'warning',
    }).catch((err) => {
      console.warn('[clientv2] failed to push rate-limit toast', err);
    });
  }

  return subtitle;
};

const parseBody = async <T,>(resp: Response): Promise<T | undefined> => {
  const text = await resp.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as T;
  } catch {
    return text as unknown as T;
  }
};

const request = async <T = any>(input: RequestInfo | URL, init?: RequestInit, throwErrors?: boolean): Promise<APIClientReturn<T>> => {
  const headers = (init || {}).headers || {}

  try {
    const resp = await fetch(input, { ...(init || {}), headers });
    const data = await parseBody<T>(resp);
    if (resp.status !== 200) {
      const serverMessage = extractServerMessage(data);
      const msg = resp.status === 429
        ? handleRateLimit(resp, serverMessage)
        : (serverMessage || `Error code ${resp.status}`);
      const err = new Error(msg) as Error & { status?: number };
      err.status = resp.status;
      if (throwErrors) {
        throw err;
      }
      return [undefined, { error: err, response: resp }, msg];
    }
    return [data, { response: resp }, undefined];
  } catch (e) {
    if (throwErrors) {
      throw e;
    }
    return [undefined, { error: e }, getErrorMessage(e)]
  }
}

export type BaseAPIClientOptions = {
  baseUrl: string;
  queryString?: string;
  getBearer?: () => Promise<string | undefined>;
  throwErrors?: boolean;
}

export class BaseAPIClientV2 {
  options: BaseAPIClientOptions;

  constructor(options: BaseAPIClientOptions) {
    this.options = options;
  }

  urlFor(endpoint: string) {
    return (
      `${this.options.baseUrl}` +
      (endpoint.startsWith('/') || this.options.baseUrl.endsWith('/') ? endpoint : `/${endpoint}`)
    )
  }

  async getHeaders(): Promise<HeadersInit> {
    let bearer: string | undefined = undefined;
    if (this.options.getBearer) {
      bearer = await this.options.getBearer();
    }

    const sessionId = getFindexSessionId();

    return {
      ...(bearer
        ? {
            Authorization: 'Bearer ' + bearer
          }
        : {}),
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...(sessionId ? {
        'X-findexSessionId': sessionId,
        'access-control-expose-headers': 'X-findexSessionId'
      } : {})
    }
  }

  async get<T = any>(endpoint: string, args: APIClientRequestArgs = {})  {
    const headers = await this.getHeaders()
    const resource = this.urlFor(endpoint)
    const mergedHeaders = { ...headers, ...(args.request?.headers || {}) } as Record<string, string>
    const init: RequestInit = { method: 'GET', headers: mergedHeaders, mode: args.request?.mode, redirect: args.request?.redirect }

    // Skip dedupe whenever the caller passes a custom request init. The key
    // only encodes (url, Authorization, sessionId, throwErrors), so we cannot
    // safely coalesce calls that diverge on mode, redirect, or non-auth
    // headers (e.g. If-None-Match, Accept-Language, Cache-Control) — two such
    // callers expecting different response shapes would receive the same
    // payload. `whoami(token)` is the only documented caller passing custom
    // headers today; letting it issue its own fetch is the safe default.
    if (args.request) {
      return request<T>(resource, init, this.options.throwErrors)
    }

    // Coalesce concurrent identical GETs. Key includes the final Authorization
    // header so a token swap (re-login, explicit `whoami(token)`) never shares
    // a response with a different bearer. Entry is dropped on settle, so this
    // is purely in-flight dedupe — never a result cache.
    // `throwErrors` is part of the key because two clients pointing at the
    // same backend with different error modes (FindexClient with throwErrors
    // false, useFinsquid/useImportAssets with throwErrors true) could otherwise
    // coalesce and force one caller to inherit the other's error semantics.
    // Segments are separated by `\n` so variable-length URLs/tokens cannot
    // produce a collision (`\n` is rejected from URLs by every browser fetch
    // and is not valid in HTTP header values).
    const auth = mergedHeaders['Authorization'] || ''
    const sessionId = mergedHeaders['X-findexSessionId'] || ''
    const throwErrors = this.options.throwErrors ? '1' : '0'
    const key = `GET\n${resource}\n${auth}\n${sessionId}\n${throwErrors}`
    const existing = inFlightGets.get(key) as Promise<APIClientReturn<T>> | undefined
    if (existing) return existing
    const promise = request<T>(resource, init, this.options.throwErrors)
      .finally(() => { inFlightGets.delete(key) })
    inFlightGets.set(key, promise)
    return promise
  }

  async delete<T = any>(endpoint: string, args: APIClientRequestArgs = {}) {
    const headers = await this.getHeaders()
    const resource = this.urlFor(endpoint)
    return await request<T>(resource, {
      headers: {...headers, ...(args.request?.headers || {})},
      method: 'DELETE'
    }, this.options.throwErrors);
  }

  async patch<T = any>(
    endpoint: string,
    args: APIClientRequestArgs = {}
  ) {
    const headers = await this.getHeaders()
    const resource = this.urlFor(endpoint)
    return await request<T>(resource, {
      headers: {...headers, ...(args.request?.headers || {})},
      method: 'PATCH',
      body: JSON.stringify(args.payload || {})
    }, this.options.throwErrors)
  }

  async post<T = any>(
    endpoint: string,
    args: APIClientRequestArgs = {}
  ) {
    const headers = await this.getHeaders()
    const resource = this.urlFor(endpoint)
    return await request<T>(resource, {
      headers: {...headers, ...(args.request?.headers || {})},
      method: 'POST',
      body: JSON.stringify(args.payload || {})
    }, this.options.throwErrors)
  }

  async put<T = any>(
    endpoint: string,
    args: APIClientRequestArgs = {}
  ) {
    const headers = await this.getHeaders()
    const resource = this.urlFor(endpoint)
    return await request<T>(resource, {
      headers: {...headers, ...(args.request?.headers || {})},
      method: 'PUT',
      body: JSON.stringify(args.payload || {})
    }, this.options.throwErrors)
  }
}
